/**
 * Enhanced Authentication Interceptor
 * Ensures immediate logout on authentication failures
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { store } from '../store';
import { clearAuth, setSessionSettings, setPendingLogoutRedirect } from '../store/authSlice';
import { setDisconnected, setConnected } from '../store/connectionSlice';
import StorageService from './storage';
import tokenRefreshService from './tokenRefresh';
import { LogoutCoordinator } from './logoutCoordinator';
import { SessionSettings } from '../types/auth';
import { API_BASE_URL as RAW_API_BASE_URL } from './api';
import { coordinatedRefresh } from './refreshCoordinator';

const API_BASE_URL = RAW_API_BASE_URL.replace(/\/$/, '');

interface AuthError extends AxiosError {
  config?: InternalAxiosRequestConfig & {
    _retry?: boolean;
    _isAuthCheck?: boolean;
  };
}

class AuthenticationInterceptor {
  private static instance: AuthenticationInterceptor;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: any) => void;
  }> = [];
  private validationTimer: ReturnType<typeof setInterval> | null = null;
  private currentValidationIntervalMs = 5 * 60 * 1000;
  /** Guard: only one "session-status unauthenticated" flow at a time to avoid duplicate logout/refresh */
  private sessionStatusUnauthHandling = false;
  /** HTTP status of the most recent coordinated refresh. 0 = network error.
   *  Lets callers tell "the server rejected us" (401/403 → real logout) apart
   *  from "we never reached the server" (0/5xx → transient, keep the session). */
  private lastRefreshStatus = 0;

  private constructor() {
    this.setupInterceptors();
  }

  static getInstance(): AuthenticationInterceptor {
    if (!AuthenticationInterceptor.instance) {
      AuthenticationInterceptor.instance = new AuthenticationInterceptor();
    }
    return AuthenticationInterceptor.instance;
  }

  private canAttemptRefresh(): boolean {
    // If a logout is already in flight, do not attempt to refresh — the user's
    // auth state is being torn down. Refreshing here just wastes a network
    // round-trip and re-enters the 401-handling code path.
    if (LogoutCoordinator.active) {
      return false;
    }
    const authState = store.getState().auth;
    return (
      !!authState.isAuthenticated &&
      !!authState.user &&
      (authState.validationState === 'valid' || authState.validationState === 'failed')
    );
  }

  private setupInterceptors() {
    // Request Interceptor - Add authentication headers
    axios.interceptors.request.use(
      (config) => {
        // Always include credentials for cookie-based auth
        config.withCredentials = true;

        // Add timestamp to prevent caching
        if (config.method === 'GET') {
          config.params = {
            ...config.params,
            _t: Date.now()
          };
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response Interceptor - Handle authentication errors
    axios.interceptors.response.use(
      (response: AxiosResponse) => {
        // Check for authentication status in response.
        // IMPORTANT: Skip requests marked _isAuthCheck (e.g. from validateAuthentication)
        // because that method already handles authenticated:false with proper token
        // refresh logic.  If we triggered logout here the refresh would never run.
        if (response.config.url?.includes('session-status') && !(response.config as any)?._isAuthCheck) {
          const data = response.data;
          if (data && typeof data === 'object' && 'authenticated' in data) {
            if (!data.authenticated) {
              console.warn(
                `[LOGOUT-DEBUG] authInterceptor.session_status_unauthenticated | reason=session_invalid | ` +
                `requestUrl=${response.config.url} | will_try_refresh_first | timestamp=${new Date().toISOString()}`
              );
              // Try refresh first; only logout if refresh fails (prevents premature logout on transient hiccups)
              this.handleSessionStatusUnauthenticated();
            }
          }
        }
        return response;
      },
      async (error: AuthError) => {
        const originalRequest = error.config;

        // Handle network errors
        if (!error.response) {
          if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
            store.dispatch(setDisconnected({ error: 'Network error' }));
            return Promise.reject(error);
          }
        }

        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
          // Special handling for login endpoint
          if (originalRequest?.url?.includes('/auth/login')) {
            return Promise.reject(error);
          }

          // Let caller (e.g. validateAuthentication) handle refresh; do not logout here
          if ((originalRequest as any)?._isAuthCheck) {
            console.warn(
              `[LOGOUT-DEBUG] authInterceptor: 401 on _isAuthCheck request, letting caller handle refresh | ` +
              `requestUrl=${originalRequest?.url} | timestamp=${new Date().toISOString()}`
            );
            return Promise.reject(error);
          }

          if (!this.canAttemptRefresh()) {
            return Promise.reject(error);
          }

          // Try refresh once
          if (originalRequest && !(originalRequest as any)._retry) {
            (originalRequest as any)._retry = true;

            if (!this.isRefreshing) {
              this.isRefreshing = true;

              // Route through the shared coordinator so this refresh can never
              // race a refresh from api.ts / tokenRefresh.ts against the
              // rotating /auth/refresh/ endpoint (which would revoke the token
              // and force a logout). See refreshCoordinator.ts.
              const result = await coordinatedRefresh();
              this.isRefreshing = false;

              if (result.ok) {
                this.processQueue(null, true);
                if (result.data?.session_settings) {
                  this.applySessionSettings(result.data.session_settings);
                }
                return axios(originalRequest!);
              }

              this.processQueue(new Error(`Token refresh failed (status ${result.status})`), false);
              console.warn(
                `[LOGOUT-DEBUG] authInterceptor.401_refresh_failed | reason=token_refresh_failed | ` +
                `requestUrl=${originalRequest?.url} | status=401 | refreshStatus=${result.status} | ` +
                `timestamp=${new Date().toISOString()}`
              );
              // Only an explicit auth rejection ends the session. A network
              // error (status 0) or a 5xx means we never learned whether the
              // session is valid — tearing it down there is a false positive
              // (playbook §10). refreshCoordinator no longer retries network
              // errors, so this check is now load-bearing rather than incidental.
              if (result.status === 401 || result.status === 403) {
                this.handleAuthenticationFailure('token_refresh_failed');
              }
              return Promise.reject(error);
            }

            // Queue the request while refreshing
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            });
          }

          // Refresh already attempted, logout user
          console.warn(
            `[LOGOUT-DEBUG] authInterceptor.401_retry_exhausted | reason=auth_failed | ` +
            `requestUrl=${originalRequest?.url} | status=401 | timestamp=${new Date().toISOString()}`
          );
          this.handleAuthenticationFailure('auth_failed');
        }

        // Handle 403 Forbidden
        if (error.response?.status === 403) {
          // Check if it's a permission issue or authentication issue
          const errorData = error.response.data as any;
          if (errorData?.code === 'not_authenticated' || errorData?.code === 'token_invalid') {
            console.warn(
              `[LOGOUT-DEBUG] authInterceptor.403_forbidden | reason=forbidden_auth | ` +
              `requestUrl=${originalRequest?.url} | status=403 | code=${errorData?.code} | timestamp=${new Date().toISOString()}`
            );
            this.handleAuthenticationFailure('forbidden_auth');
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private processQueue(error: any, success: boolean) {
    this.failedQueue.forEach(prom => {
      if (success) {
        prom.resolve();
      } else {
        prom.reject(error);
      }
    });
    this.failedQueue = [];
  }

  /**
   * When session-status returns 200 with authenticated: false, try token refresh first.
   * Only call handleAuthenticationFailure if refresh fails or is not applicable.
   * Runs asynchronously; call from sync success callback without awaiting.
   */
  private handleSessionStatusUnauthenticated(): void {
    if (this.sessionStatusUnauthHandling) {
      return;
    }
    this.sessionStatusUnauthHandling = true;

    const done = () => {
      this.sessionStatusUnauthHandling = false;
    };

    if (!StorageService.getUser() || !this.canAttemptRefresh()) {
      this.handleAuthenticationFailure('session_invalid');
      done();
      return;
    }

    this.attemptTokenRefresh('session_status_200')
      .then((refreshed) => {
        if (refreshed) {
          return this.confirmSessionStatus();
        }
        return false;
      })
      .then((confirmed) => {
        if (confirmed) {
          if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
            console.log('[AUTH] session-status 200 unauthenticated: refresh + confirm succeeded, not logging out');
          }
          done();
          return;
        }
        // The refresh never reached the server (network error / 5xx), so we have
        // no evidence the session is actually gone — keep it (playbook §10). A
        // refresh that DID get an answer (200-then-confirm-failed, or an
        // explicit 401/403) is a genuine rejection and still logs out.
        if (this.lastRefreshStatus === 0 || this.lastRefreshStatus >= 500) {
          if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
            console.log(
              `[AUTH] session-status unauthenticated but refresh was transient ` +
              `(status ${this.lastRefreshStatus}) — keeping session`
            );
          }
          done();
          return;
        }
        this.handleAuthenticationFailure('session_invalid');
        done();
      })
      .catch(() => {
        this.handleAuthenticationFailure('session_invalid');
        done();
      });
  }

  private handleAuthenticationFailure(reason: string) {
    // Prevent concurrent logout attempts from multiple systems
    if (!LogoutCoordinator.requestLogout(reason, 'authInterceptor')) {
      return;
    }

    const authState = store.getState().auth;
    console.warn(
      `[LOGOUT-DEBUG] authInterceptor.handleAuthenticationFailure | reason=${reason} | ` +
      `user=${authState.user?.username ?? 'null'} | userId=${authState.user?.id ?? 'null'} | ` +
      `wasAuthenticated=${authState.isAuthenticated} | ` +
      `url=${window.location.href} | timestamp=${new Date().toISOString()}`
    );
    console.trace('[LOGOUT-DEBUG] authInterceptor.handleAuthenticationFailure stack trace');

    // Clear all authentication state
    store.dispatch(clearAuth());
    store.dispatch(setSessionSettings(null));
    tokenRefreshService.stopAutoRefresh();
    if (this.validationTimer) {
      clearInterval(this.validationTimer);
      this.validationTimer = null;
    }

    // Clear local storage
    localStorage.removeItem('hospital_user');
    localStorage.removeItem('hospital_token_expiry');
    localStorage.removeItem('hospital_admin_user');

    // Use soft redirect via Redux to avoid destroying React component state (e.g., unsaved form text)
    const currentPath = window.location.pathname;
    if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
      const errorMessage = this.getErrorMessage(reason);
      const loginPath = currentPath.startsWith('/onecare') ? '/onecare/login' : '/login';
      store.dispatch(setPendingLogoutRedirect({
        path: `${loginPath}?redirect=${encodeURIComponent(currentPath)}`,
        error: errorMessage
      }));
    }
  }

  private getErrorMessage(reason: string): string {
    const messages: Record<string, string> = {
      'session_invalid': 'Your session has expired. Please log in again.',
      'auth_check_failed': 'Authentication verification failed.',
      'token_refresh_failed': 'Unable to refresh your session. Please log in again.',
      'auth_failed': 'Authentication failed. Please log in again.',
      'forbidden_auth': 'You do not have permission to access this resource.',
      'manual_check_failed': 'Your session expired and could not be refreshed automatically.',
      'validation_error': 'We were unable to confirm your session. Please log in again.',
      'default': 'Your session has ended. Please log in again.'
    };
    return messages[reason] || messages.default;
  }

  private async attemptTokenRefresh(source: string): Promise<boolean> {
    if (this.isRefreshing) {
      return new Promise((resolve) => {
        this.failedQueue.push({
          resolve: () => resolve(true),
          reject: () => resolve(false)
        });
      });
    }

    this.isRefreshing = true;

    // Single coordinated network refresh (shared across api.ts / tokenRefresh.ts)
    // to avoid the refresh-token rotation race. See refreshCoordinator.ts.
    const result = await coordinatedRefresh();
    this.lastRefreshStatus = result.status;
    this.isRefreshing = false;

    if (result.ok) {
      this.processQueue(null, true);
      if (result.data?.session_settings) {
        this.applySessionSettings(result.data.session_settings);
      }
      return true;
    }

    const error = new Error(`Token refresh during ${source} returned status ${result.status}`);
    this.processQueue(error, false);
    console.warn(`[AUTH] Refresh response during ${source} was not successful`, result.status);
    return false;
  }

  private async confirmSessionStatus(): Promise<boolean> {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/session-status/`, {
        withCredentials: true,
        _isAuthCheck: true
      } as any);

      const data = response.data;
      const authenticated = Boolean(data && typeof data === 'object' && data.authenticated);
      if (data && typeof data === 'object' && data.session_settings) {
        this.applySessionSettings(data.session_settings);
      }
      if (authenticated) {
        store.dispatch(setConnected(true));
      }
      return authenticated;
    } catch (error) {
      console.error('[AUTH] Unable to confirm session after refresh attempt', error);
      return false;
    }
  }

  private applySessionSettings(settings: SessionSettings) {
    // Guard: if the backend returned unresolved defaults (hospital_id is null)
    // but we already have hospital-specific settings, keep the existing ones.
    const currentSettings = StorageService.getSessionSettings();
    if (currentSettings?.hospital_id && !settings.hospital_id) {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log(
          `[SESSION] Ignoring default session settings from backend (hospital_id=null); keeping hospital-specific settings (hospital_id=${currentSettings.hospital_id})`
        );
      }
      return;
    }

    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      const idle = settings.idle_timeout_minutes ?? '?';
      const max = settings.max_login_duration_minutes ?? '?';
      const check = settings.session_check_interval_minutes ?? '?';
      const hid = settings.hospital_id ?? 'null';
      console.log(
        `[SESSION] Applied session settings from backend: hospital_id=${hid} idle_timeout_minutes=${idle} max_login_duration_minutes=${max} session_check_interval_minutes=${check}`
      );
    }
    StorageService.setSessionSettings(settings);
    this.updateValidationInterval(settings.session_check_interval_minutes);
    tokenRefreshService.startAutoRefresh(true);
  }

  // Public method to manually trigger authentication check
  async validateAuthentication(): Promise<boolean> {
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[SESSION] validateAuthentication() running');
    }
    try {
      // Get session settings
      const sessionSettings = StorageService.getSessionSettings();
      const anyTimeoutEnabled = sessionSettings?.max_login_duration_enabled || sessionSettings?.idle_timeout_enabled;

      // If both timeout features are disabled and we have a user, consider the session valid
      if (!anyTimeoutEnabled && StorageService.getUser()) {
        store.dispatch(setConnected(true));
        // Update last activity time
        StorageService.updateLastActivity();
        return true;
      }

      let response = await axios.get(`${API_BASE_URL}/auth/session-status/`, {
        withCredentials: true,
        _isAuthCheck: true
      } as any);

      let data = response.data;
      const hasSessionSettings = data && typeof data === 'object' && data.session_settings;
      if (hasSessionSettings) {
        this.applySessionSettings(data.session_settings);
      }
      if (data && typeof data === 'object' && 'authenticated' in data) {
        let authenticated = Boolean(data.authenticated);
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log(
            `[SESSION] session-status OK: authenticated=${authenticated} session_settings_present=${hasSessionSettings}`
          );
        }
        if (!authenticated) {
          // Retry session-status once after a short delay to avoid logout on transient backend/cookie hiccups
          await new Promise((r) => setTimeout(r, 1500));
          response = await axios.get(`${API_BASE_URL}/auth/session-status/`, {
            withCredentials: true,
            _isAuthCheck: true
          } as any);
          data = response.data;
          if (data && typeof data === 'object' && data.session_settings) {
            this.applySessionSettings(data.session_settings);
          }
          authenticated = Boolean(data && typeof data === 'object' && data.authenticated);
          if (authenticated) {
            store.dispatch(setConnected(true));
            StorageService.updateLastActivity();
            return true;
          }

          const refreshed = await this.attemptTokenRefresh('manual_check');
          if (refreshed && await this.confirmSessionStatus()) {
            // Update last activity time after successful refresh
            StorageService.updateLastActivity();
            return true;
          }

          // Final guard before destroying the session: the DRF access token and
          // the refresh token are INDEPENDENT. A logout-wide refresh-token revoke
          // (revoke_all_user_tokens) makes /auth/refresh/ fail while a still-live
          // access token keeps authorizing normal API + WS calls. Don't log the
          // user out if the access token is demonstrably still valid — probe a
          // Token-auth-REQUIRED endpoint and, if it succeeds, stay logged in and
          // just stop hammering the broken refresh.
          if (await this.probeAccessTokenStillValid()) {
            if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
              console.log('[SESSION] manual_check: refresh failed but access token still valid — staying logged in');
            }
            store.dispatch(setConnected(true));
            StorageService.updateLastActivity();
            tokenRefreshService.stopAutoRefresh();
            return true;
          }

          if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
            console.log('[SESSION] handleAuthenticationFailure: reason=manual_check_failed');
          }
          this.handleAuthenticationFailure('manual_check_failed');
          return false;
        }
        store.dispatch(setConnected(true));
        // Update last activity time after successful authentication
        StorageService.updateLastActivity();
        return true;
      }
      return false;
    } catch (error) {
      const refreshed = await this.attemptTokenRefresh('manual_check_error');
      if (refreshed && await this.confirmSessionStatus()) {
        // Update last activity time after successful refresh
        StorageService.updateLastActivity();
        return true;
      }

      // Same guard as the manual_check path: keep the user logged in if the
      // access token still works, even though session-status errored + refresh
      // failed (e.g. a transient network blip on session-status).
      if (await this.probeAccessTokenStillValid()) {
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log('[SESSION] validation_error: access token still valid — staying logged in');
        }
        store.dispatch(setConnected(true));
        StorageService.updateLastActivity();
        return true;
      }

      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log('[SESSION] handleAuthenticationFailure: reason=validation_error');
      }
      this.handleAuthenticationFailure('validation_error');
      return false;
    }
  }

  /**
   * Probe a Token-auth-REQUIRED endpoint to confirm the DRF access token is
   * still valid. Returns true only on a 2xx. Used as a last guard before a
   * validation-driven logout so a still-usable session is never destroyed just
   * because the (independent) refresh token was revoked or session-status hiccuped.
   * Marked _isAuthCheck so the global interceptor does not itself react to a 401.
   *
   * MUST be a POST. `/auth/websocket-token/` is declared `@api_view(['POST'])`
   * (accounts/websocket_auth.py), so the GET this used to send returned 405 on a
   * PERFECTLY HEALTHY session — the probe read that as failure and the guard
   * therefore never once saved a session in its entire existence. If you change
   * the endpoint, re-check the method here; a wrong verb turns this back into
   * dead code that silently green-lights every forced logout.
   */
  private async probeAccessTokenStillValid(): Promise<boolean> {
    try {
      const resp = await axios.post(`${API_BASE_URL}/auth/websocket-token/`, {}, {
        withCredentials: true,
        _isAuthCheck: true,
        validateStatus: () => true,
      } as any);
      return resp.status >= 200 && resp.status < 300;
    } catch {
      return false;
    }
  }

  // Periodic session validation
  startPeriodicValidation(intervalMinutes?: number) {
    const sessionSettings = StorageService.getSessionSettings();
    const minutes = intervalMinutes ?? sessionSettings?.session_check_interval_minutes ?? 5;
    const intervalMs = Math.max(1, minutes) * 60 * 1000;

    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log(
        `[SESSION] Periodic validation started, intervalMs=${intervalMs} (session_check_interval_minutes=${minutes})`
      );
    }

    if (this.validationTimer) {
      clearInterval(this.validationTimer);
    }

    this.currentValidationIntervalMs = intervalMs;
    this.validationTimer = setInterval(async () => {
      const state = store.getState();
      if (state.auth.isAuthenticated) {
        await this.validateAuthentication();
      }
    }, this.currentValidationIntervalMs);
  }

  updateValidationInterval(intervalMinutes?: number) {
    this.startPeriodicValidation(intervalMinutes);
  }
}

// Export singleton instance
export const authInterceptor = AuthenticationInterceptor.getInstance();

tokenRefreshService.registerSessionSettingsListener((settings) => {
  store.dispatch(setSessionSettings(settings));
  authInterceptor.updateValidationInterval(settings.session_check_interval_minutes);
});

// Auto-start periodic validation
authInterceptor.startPeriodicValidation();
